import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { Formik, Form, Field, FieldProps } from 'formik';
import { PersonDTO } from '../project.type'; // Import your type
import { Autocomplete, Divider, Grid, TextField, createFilterOptions } from '@mui/material';
import { Typography } from 'components/index';
import { capitalize, compareCaseInsensitive, isPostalCodeValid } from 'common/utils/common.util';
import * as Yup from 'yup'; // Import Yup
import useLookups from '../../../hooks/useLookups';
import { LOOKUP_KEYS } from '../../../common/constants/lookups';
import { City, State } from '../../../components/lookups/lookups.type';
import { getCityString, getStateLabel, isValidCityState } from '../../../components/lookups/lookups.util';
import { validateCityState } from '../../../components/lookups/lookups.api';
const DEBOUNCE_MS = 500;

// Falls back to a Google Geocode check when the local Cities lookup misses.
const isValidCityStateWithFallback = async (cityOptions: City[], city?: string | null, state?: string | null) => {
  if (isValidCityState(cityOptions, city, state)) return true;
  if (!city?.trim() || !state?.trim()) return false;
  try {
    const result = await validateCityState(city, state);
    return !!result?.isValid;
  } catch {
    return true; // service unavailable — don't block the user on a possibly-real address
  }
};

interface Props {
  ClientInfo: PersonDTO;
  BorrowerInfo: PersonDTO;
  onUpdate: (clientInfo: PersonDTO, borrowerInfo: PersonDTO) => void;
  onValidationChange: (isValid: boolean) => void;
}

export interface ClientAndBorrowerInfoRef {
  submitForm: () => void;
}

export const ClientAndBorrowerInfo = forwardRef<ClientAndBorrowerInfoRef, Props>(
  ({ ClientInfo, BorrowerInfo, onUpdate, onValidationChange }, ref) => {
    const formikRef = React.useRef<any>();
    const stateOptions = useLookups(LOOKUP_KEYS.States) as State[];
    const cityOptions = useLookups(LOOKUP_KEYS.Cities) as City[];
    const cityOptionStrings = cityOptions.map(getCityString);
    const [selectedClientState, setSelectedClientState] = useState('');
    const [selectedBorrowerState, setSelectedBorrowerState] = useState('');
    const [clientCitySelectedFromList, setClientCitySelectedFromList] = useState(false);
    const [borrowerCitySelectedFromList, setBorrowerCitySelectedFromList] = useState(false);

    const validationSchema = Yup.object({
      ClientInfo: Yup.object({
        address: Yup.object({
          postalCode: Yup.string()
            .matches(
              /^(?:\d{5}(?:-\d{4})?)?$/,
              'Invalid Client Postal Code. Please enter a valid postal code in the format 12345 or 12345-6789',
            )
            .notRequired(),
          state: Yup.string().test('city-state-combo', 'This city/state combination does not exist', (value, context) =>
            isValidCityStateWithFallback(cityOptions, context.parent.city, value),
          ),
        }),
      }),
      BorrowerInfo: Yup.object({
        address: Yup.object({
          postalCode: Yup.string()
            .matches(
              /^(?:\d{5}(?:-\d{4})?)?$/,
              'Invalid Borrower Postal Code. Please enter a valid postal code in the format 12345 or 12345-6789',
            )
            .notRequired(), // Optional field, remove if postal code is required
          state: Yup.string().test('city-state-combo', 'This city/state combination does not exist', (value, context) =>
            isValidCityStateWithFallback(cityOptions, context.parent.city, value),
          ),
        }),
      }),
    });

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        if (formikRef.current) {
          formikRef.current.submitForm();
        }
      },
    }));

    const [clientPostalCodeError, setClientPostalCodeError] = useState('');
    const [borrowerPostalCodeError, setBorrowerPostalCodeError] = useState('');

    const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'client' | 'borrower') => {
      const { value } = e.target;

      if (type === 'client') {
        formikRef.current.setFieldValue('ClientInfo.address.postalCode', value);
        if (isPostalCodeValid(value)) {
          setClientPostalCodeError('');
        } else {
          setClientPostalCodeError('Invalid Client Postal Code. Please enter a valid postal code in the format 12345 or 12345-6789');
        }
      } else if (type === 'borrower') {
        formikRef.current.setFieldValue('BorrowerInfo.address.postalCode', value);
        if (isPostalCodeValid(value)) {
          setBorrowerPostalCodeError('');
        } else {
          setBorrowerPostalCodeError('Invalid Borrower Postal Code. Please enter a valid postal code in the format 12345 or 12345-6789');
        }
      }
    };

    const validateForm = async (values: any) => {
      try {
        await validationSchema.validate(values, { abortEarly: false });
        onValidationChange(true);
      } catch (error) {
        onValidationChange(false);
      }
    };

    return (
      <Formik
        innerRef={formikRef}
        initialValues={{ ClientInfo, BorrowerInfo }}
        validationSchema={validationSchema}
        validateOnChange={false} // state fields' test hits Google Geocode; validate on blur/submit only
        onSubmit={({ ClientInfo, BorrowerInfo }, actions) => {
          onUpdate(ClientInfo, BorrowerInfo);
          actions.setSubmitting(false);
        }}
        validate={validateForm}
      >
        <Form noValidate autoComplete='off'>
          <Grid container spacing={2} style={{ marginTop: '20px' }}>
            <Grid size={{ xs: 12, md: 5 }}>
              <div>
                <Typography variant='h6' style={{ marginBottom: '20px' }}>
                  Client Information
                </Typography>
                <div>
                  <Field as={TextField} fullWidth label='Client Name' name='ClientInfo.name' variant='outlined' margin='normal' />
                </div>
                <div>
                  <Field
                    as={TextField}
                    fullWidth
                    label='LoanOfficerName'
                    name='ClientInfo.loanOfficerName'
                    variant='outlined'
                    margin='normal'
                  />
                </div>
                <div>
                  <Field as={TextField} fullWidth label='Street' name='ClientInfo.address.street' variant='outlined' margin='normal' />
                </div>
                <div>
                  <Field name='ClientInfo.address.city'>
                    {({ field, form }: FieldProps) => (
                      <Autocomplete
                        {...field}
                        id='client-city-autocomplete'
                        freeSolo
                        options={cityOptionStrings}
                        getOptionLabel={option => (typeof option === 'string' ? option : option)}
                        autoComplete
                        autoHighlight
                        filterOptions={createFilterOptions({ limit: 10, matchFrom: 'start' })}
                        onInputChange={(_, value) => {
                          form.setFieldValue(field.name, value ?? '');
                          setClientCitySelectedFromList(false);
                        }}
                        onChange={(event, newValue) => {
                          const isOptionFromList = newValue && typeof newValue === 'string' && cityOptionStrings.includes(newValue);
                          if (isOptionFromList && newValue) {
                            const parts = newValue.split(', ');
                            const city = parts[0] ?? '';
                            const stateCode = parts[1] ?? '';
                            form.setFieldValue(field.name, city);
                            const matchingState = stateOptions.find(option => compareCaseInsensitive(option.stateCode, stateCode));
                            const stateName = capitalize(matchingState?.name) ?? '';
                            setSelectedClientState(stateName);
                            form.setFieldValue('ClientInfo.address.state', stateName);
                            setClientCitySelectedFromList(true);
                          } else {
                            setClientCitySelectedFromList(false);
                            if (newValue !== null) {
                              form.setFieldValue(field.name, typeof newValue === 'string' ? newValue : '');
                            }
                            if (!newValue) setSelectedClientState('');
                          }
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label='City'
                            margin='normal'
                            sx={{
                              '& .MuiInputBase-input': { height: '10px', fontSize: '0.875rem', padding: '0px 0px' },
                            }}
                          />
                        )}
                      />
                    )}
                  </Field>
                </div>
                <div style={{ marginTop: '16px', marginBottom: '24px' }}>
                  <Field name='ClientInfo.address.state' margin='normal'>
                    {({ field, form }: FieldProps) =>
                      clientCitySelectedFromList ? (
                        <TextField
                          value={field.value || selectedClientState}
                          placeholder='State'
                          label='State'
                          type='text'
                          fullWidth
                          inputProps={{ readOnly: true }}
                          sx={{ pointerEvents: 'none', '& .MuiInputBase-input': { height: '10px', fontSize: '0.875rem' } }}
                        />
                      ) : (
                        <Autocomplete
                          freeSolo
                          options={stateOptions}
                          getOptionLabel={option => (typeof option === 'string' ? option : (getStateLabel(option) ?? ''))}
                          value={field.value || ''}
                          inputValue={field.value || ''}
                          onInputChange={(_, newInputValue, reason) => {
                            if (reason === 'input') {
                              form.setFieldValue(field.name, newInputValue, false);
                            }
                          }}
                          onChange={(_, newValue) => {
                            const stateName = typeof newValue === 'string' ? newValue : newValue ? (getStateLabel(newValue) ?? '') : '';
                            form.setFieldValue(field.name, stateName);
                          }}
                          filterOptions={createFilterOptions({ limit: 10, matchFrom: 'start' })}
                          renderInput={params => (
                            <TextField
                              {...params}
                              label='State'
                              placeholder='State'
                              margin='normal'
                              onBlur={() => form.setFieldTouched(field.name, true)}
                              error={Boolean((form.errors as any)?.ClientInfo?.address?.state)}
                              helperText={(form.errors as any)?.ClientInfo?.address?.state || ''}
                              sx={{ '& .MuiInputBase-input': { height: '10px', fontSize: '0.875rem', padding: '0px 0px' } }}
                            />
                          )}
                        />
                      )
                    }
                  </Field>
                </div>
                <div>
                  <Field
                    as={TextField}
                    fullWidth
                    label='Postal Code'
                    name='ClientInfo.address.postalCode'
                    variant='outlined'
                    margin='normal'
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePostalCodeChange(e, 'client')}
                    error={Boolean(clientPostalCodeError)}
                    helperText={clientPostalCodeError}
                  />
                </div>
              </div>
            </Grid>

            <Grid size={{ xs: 12, md: 1 }}>
              <Divider orientation='vertical' flexItem />
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <div>
                <Typography variant='h6' style={{ marginBottom: '20px' }}>
                  Borrower Information
                </Typography>
                <div>
                  <Field as={TextField} fullWidth label='Borrower Name' name='BorrowerInfo.name' variant='outlined' margin='normal' />
                </div>
                <div>
                  <Field as={TextField} fullWidth label='Company' name='BorrowerInfo.company' variant='outlined' margin='normal' />
                </div>
                <div>
                  <Field as={TextField} fullWidth label='Street' name='BorrowerInfo.address.street' variant='outlined' margin='normal' />
                </div>
                <div>
                  <Field name='BorrowerInfo.address.city'>
                    {({ field, form }: FieldProps) => (
                      <Autocomplete
                        {...field}
                        id='borrower-city-autocomplete'
                        freeSolo
                        options={cityOptionStrings}
                        getOptionLabel={option => (typeof option === 'string' ? option : option)}
                        autoComplete
                        autoHighlight
                        filterOptions={createFilterOptions({ limit: 10, matchFrom: 'start' })}
                        onInputChange={(_, value) => {
                          form.setFieldValue(field.name, value ?? '');
                          setBorrowerCitySelectedFromList(false);
                        }}
                        onChange={(event, newValue) => {
                          const isOptionFromList = newValue && typeof newValue === 'string' && cityOptionStrings.includes(newValue);
                          if (isOptionFromList && newValue) {
                            const parts = newValue.split(', ');
                            const city = parts[0] ?? '';
                            const stateCode = parts[1] ?? '';
                            form.setFieldValue(field.name, city);
                            const matchingState = stateOptions.find(option => compareCaseInsensitive(option.stateCode, stateCode));
                            const stateName = capitalize(matchingState?.name) ?? '';
                            setSelectedBorrowerState(stateName);
                            form.setFieldValue('BorrowerInfo.address.state', stateName);
                            setBorrowerCitySelectedFromList(true);
                          } else {
                            setBorrowerCitySelectedFromList(false);
                            if (newValue !== null) {
                              form.setFieldValue(field.name, typeof newValue === 'string' ? newValue : '');
                            }
                            if (!newValue) setSelectedBorrowerState('');
                          }
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label='City'
                            margin='normal'
                            sx={{
                              '& .MuiInputBase-input': { height: '10px', fontSize: '0.875rem', padding: '0px 0px' },
                            }}
                          />
                        )}
                      />
                    )}
                  </Field>
                </div>
                <div style={{ marginTop: '16px', marginBottom: '24px' }}>
                  <Field name='BorrowerInfo.address.state' margin='normal'>
                    {({ field, form }: FieldProps) =>
                      borrowerCitySelectedFromList ? (
                        <TextField
                          value={field.value || selectedBorrowerState}
                          placeholder='State'
                          label='State'
                          type='text'
                          fullWidth
                          inputProps={{ readOnly: true }}
                          sx={{ pointerEvents: 'none', '& .MuiInputBase-input': { height: '10px', fontSize: '0.875rem' } }}
                        />
                      ) : (
                        <Autocomplete
                          freeSolo
                          options={stateOptions}
                          getOptionLabel={option => (typeof option === 'string' ? option : (getStateLabel(option) ?? ''))}
                          value={field.value || ''}
                          inputValue={field.value || ''}
                          onInputChange={(_, newInputValue, reason) => {
                            if (reason === 'input') {
                              form.setFieldValue(field.name, newInputValue, false);
                            }
                          }}
                          onChange={(_, newValue) => {
                            const stateName = typeof newValue === 'string' ? newValue : newValue ? (getStateLabel(newValue) ?? '') : '';
                            form.setFieldValue(field.name, stateName);
                          }}
                          filterOptions={createFilterOptions({ limit: 10, matchFrom: 'start' })}
                          renderInput={params => (
                            <TextField
                              {...params}
                              label='State'
                              placeholder='State'
                              margin='normal'
                              onBlur={() => form.setFieldTouched(field.name, true)}
                              error={Boolean((form.errors as any)?.BorrowerInfo?.address?.state)}
                              helperText={(form.errors as any)?.BorrowerInfo?.address?.state || ''}
                              sx={{ '& .MuiInputBase-input': { height: '10px', fontSize: '0.875rem', padding: '0px 0px' } }}
                            />
                          )}
                        />
                      )
                    }
                  </Field>
                </div>
                <div>
                  <Field
                    as={TextField}
                    fullWidth
                    label='Postal Code'
                    name='BorrowerInfo.address.postalCode'
                    variant='outlined'
                    margin='normal'
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePostalCodeChange(e, 'borrower')}
                    error={Boolean(borrowerPostalCodeError)}
                    helperText={borrowerPostalCodeError}
                  />
                </div>
              </div>
            </Grid>
          </Grid>
        </Form>
      </Formik>
    );
  },
);

ClientAndBorrowerInfo.displayName = 'ClientAndBorrowerInfo';
